<template>
  <div class="fixed z-40" :style="containerStyles">
    <transition-group name="announcement" tag="div" class="space-y-4">
      <div
        v-for="(announcement, index) in visibleAnnouncements"
        :key="announcement.id"
        class="bg-white rounded-xl transform"
        :style="{
          ...cardStyles,
          ...getAnimationStyles(announcement.id),
          marginTop: index > 0 ? (isMobile ? '-6px' : '-8px') : '0px',
          zIndex: 1000 - index,
          position: 'relative',
        }"
      >
        <!-- 🆕 NEW: Conditional layout based on content -->
        <!-- Simple layout: Only title, no message/content - WHOLE BOX CLICKABLE -->
        <div
          v-if="isSimpleTextOnly(announcement)"
          class="flex items-center justify-between gap-3 cursor-pointer rounded-lg"
          :style="{
            margin: '-8px',
            padding: '8px',
            transition: 'background-color 0.2s ease',
            backgroundColor: 'transparent',
          }"
          @click="handleTextAnnouncementClick(announcement.id)"
          @mouseenter="handleBoxHover($event, true)"
          @mouseleave="handleBoxHover($event, false)"
        >
          <!-- Title (not clickable separately) -->
          <h4
            :class="isMobile ? 'text-xs' : 'text-sm'"
            class="font-medium text-gray-900 flex-1 min-w-0 pointer-events-none"
          >
            {{ announcement.title }}
          </h4>

          <!-- Close button (prevent event bubbling) -->
          <button
            @click.stop="dismissAnnouncement(announcement.id)"
            class="cursor-pointer text-gray-400 flex-shrink-0 rounded-full"
            :style="{
              ...closeButtonStyles,
              transition: 'all 0.2s ease',
            }"
            @mouseenter="handleCloseHover($event, true)"
            @mouseleave="handleCloseHover($event, false)"
          >
            <svg
              :class="isMobile ? 'w-3 h-3' : 'w-4 h-4'"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        <!-- Complex layout: Has message or special content -->
        <div v-else class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <!-- Title -->
            <h4
              v-if="announcement.title"
              @click="navigateToChat"
              :class="isMobile ? 'text-xs mb-1' : 'text-sm mb-2'"
              class="font-medium text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
            >
              {{ announcement.title }}
            </h4>

            <!-- Message -->
            <p
              v-if="announcement.message"
              :class="isMobile ? 'mb-2' : 'mb-3'"
              class="text-xs text-gray-600 line-clamp-2"
            >
              {{ announcement.message }}
            </p>

            <!-- Type-specific content -->
            <!-- Image Type -->
            <div v-if="announcement.type === 'image' && announcement.imageUrl">
              <img
                :src="announcement.imageUrl"
                :class="isMobile ? 'h-20' : 'h-24'"
                class="w-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                :style="imageStyles"
                alt="Preview"
                @click="openLink(announcement.linkUrl)"
              />
            </div>

            <!-- FAQ Type -->
            <div v-if="announcement.type === 'faq' && announcement.faqTitle">
              <div
                @click="handleFaqClick(announcement)"
                class="flex items-center space-x-3 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
                :style="faqStyles"
              >
                <!-- FAQ Icon -->
                <svg
                  :class="isMobile ? 'w-4 h-4' : 'w-5 h-5'"
                  class="text-gray-500 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>

                <!-- FAQ Content -->
                <div class="flex-1 min-w-0">
                  <div class="text-xs font-medium text-gray-700 truncate">
                    {{ announcement.faqTitle }}
                  </div>
                  <div class="text-xs text-gray-500 mt-0.5">FAQ</div>
                </div>

                <!-- Arrow Icon -->
                <svg
                  class="w-3 h-3 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>

            <!-- Link Type -->
            <div v-if="announcement.type === 'link' && announcement.linkText">
              <button
                :class="isMobile ? 'text-xs px-2 py-1' : 'text-sm px-3 py-1.5'"
                class="text-white rounded-full font-medium"
                :style="{
                  ...linkButtonStyles,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                }"
                @click="openLink(announcement.linkUrl)"
                @mouseenter="handleButtonHover($event, true)"
                @mouseleave="handleButtonHover($event, false)"
              >
                {{ announcement.linkText }}
              </button>
            </div>
          </div>

          <!-- Close button -->
          <button
            @click="dismissAnnouncement(announcement.id)"
            class="cursor-pointer text-gray-400 ml-2 flex-shrink-0 rounded-full"
            :style="{
              ...closeButtonStyles,
              transition: 'all 0.2s ease',
            }"
            @mouseenter="handleCloseHover($event, true)"
            @mouseleave="handleCloseHover($event, false)"
          >
            <svg
              :class="isMobile ? 'w-3 h-3' : 'w-4 h-4'"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
export default {
  name: "AnnouncementCard",
  props: {
    announcements: {
      type: Array,
      default: () => [],
    },
    announcementsEnabled: {
      type: Boolean,
      default: false,
    },
    isWidgetOpen: {
      type: Boolean,
      default: false,
    },
    bubbleColor: {
      type: String,
      default: "#1a1a1a",
    },
  },
  data() {
    return {
      dismissedAnnouncements: [],
      widgetClosedTime: null,
      timers: [],
      forceUpdateTrigger: 0,
      isMobile: false,
      // 🆕 Animation state for each announcement
      animationStates: new Map(),
      animationInterval: null,
    };
  },
  computed: {
    containerStyles() {
      return {
        bottom: "80px",
        right: "16px",
        maxWidth: this.isMobile ? "240px" : "300px",
      };
    },

    cardStyles() {
      return {
        padding: this.isMobile ? "8px" : "12px",
        border: "1px solid #e5e7eb",
        boxShadow: this.isMobile
          ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
          : "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
      };
    },

    imageStyles() {
      return {
        border: "1px solid #e5e7eb",
      };
    },

    faqStyles() {
      return {
        padding: this.isMobile ? "6px" : "8px",
        backgroundColor: "#fafafa",
        border: "1px solid #e5e7eb",
      };
    },

    closeButtonStyles() {
      return {
        padding: this.isMobile ? "2px" : "4px",
      };
    },

    // 🆕 NEW: Dynamic button styles using bubble color
    linkButtonStyles() {
      const baseColor = this.bubbleColor || "#1a1a1a";
      return {
        backgroundColor: baseColor,
      };
    },

    timeSinceWidgetClosed() {
      this.forceUpdateTrigger;

      if (!this.widgetClosedTime) return 0;
      return Date.now() - this.widgetClosedTime;
    },

    visibleAnnouncements() {
      this.forceUpdateTrigger;

      if (
        this.isWidgetOpen ||
        !this.announcementsEnabled ||
        !this.announcements.length
      ) {
        return [];
      }

      const now = Date.now();
      const closedTime = this.widgetClosedTime || now;

      const filtered = this.announcements
        .filter((announcement) => {
          if (announcement.enabled === false) {
            return false;
          }

          if (announcement.schedule && announcement.schedule !== "always") {
            return false;
          }

          if (this.dismissedAnnouncements.includes(announcement.id)) {
            return false;
          }

          let delaySeconds = 0;

          if (announcement.delay !== undefined && announcement.delay !== null) {
            if (typeof announcement.delay === "string") {
              delaySeconds = parseInt(announcement.delay, 10);
              if (isNaN(delaySeconds)) {
                console.warn("Invalid delay value:", announcement.delay);
                delaySeconds = 0;
              }
            } else if (typeof announcement.delay === "number") {
              delaySeconds = announcement.delay;
            }
          }

          const delayMs = delaySeconds * 1000;
          const timePassed = now - closedTime;

          if (timePassed < delayMs) {
            return false;
          }

          return true;
        })
        .sort((a, b) => {
          // 🔧 FIXED: Sort by when they actually appear (delay), not order
          const delayA = parseInt(a.delay) || 0;
          const delayB = parseInt(b.delay) || 0;

          // Earlier delays appear first in the array (bottom visually)
          return delayA - delayB;
        })
        .slice(0, 3);

      // 🆕 Initialize animation states for new announcements
      filtered.forEach((announcement) => {
        if (!this.animationStates.has(announcement.id)) {
          this.animationStates.set(announcement.id, {
            opacity: 0,
            transform: "translateX(100%) scale(0.9)",
            isEntering: true,
            isLeaving: false,
          });

          // Trigger enter animation
          this.$nextTick(() => {
            this.animateIn(announcement.id);
          });
        }
      });

      return filtered;
    },
  },
  watch: {
    isWidgetOpen(newVal, oldVal) {
      if (newVal === false && oldVal === true) {
        this.widgetClosedTime = Date.now();
        this.startAnnouncementTimers();
        // 🆕 Start shimmer when widget closes
        this.startShimmerAnimation();
      } else if (newVal === true) {
        this.clearAllTimers();
        // 🆕 Stop shimmer when widget opens
        this.stopShimmerAnimation();
      }
    },

    announcements: {
      handler(newAnnouncements, oldAnnouncements) {
        if (!this.isWidgetOpen && newAnnouncements?.length > 0) {
          this.startAnnouncementTimers();
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.checkMobile();
    this.loadDismissedAnnouncements();

    if (!this.isWidgetOpen) {
      this.widgetClosedTime = Date.now();
      this.startAnnouncementTimers();
      // 🆕 Start subtle shimmer animation
      this.startShimmerAnimation();
    }

    window.addEventListener("resize", this.checkMobile);
  },
  beforeUnmount() {
    this.clearAllTimers();
    this.stopShimmerAnimation();
    window.removeEventListener("resize", this.checkMobile);
  },
  methods: {
    // 🆕 NEW: Check if announcement is simple text only (title only, no message or special content)
    isSimpleTextOnly(announcement) {
      return (
        (announcement.title &&
          !announcement.message &&
          announcement.type !== "image" &&
          announcement.type !== "faq" &&
          announcement.type !== "link") ||
        (announcement.title &&
          announcement.type === "text" &&
          !announcement.message)
      );
    },

    // 🆕 NEW: Navigate to chat screen
    navigateToChat() {
      console.log("📱 Navigating to chat from announcement title click");
      this.$emit("navigate-to-chat", {
        action: "navigate-to-chat",
        screen: "ChatScreen",
      });
    },

    // 🆕 NEW: Handle text announcement click (navigate + dismiss)
    handleTextAnnouncementClick(announcementId) {
      console.log(
        "📱 Text announcement clicked - navigating to chat and dismissing"
      );

      // First navigate to chat
      this.navigateToChat();

      // Then dismiss the announcement after a short delay
      setTimeout(() => {
        this.dismissAnnouncement(announcementId);
      }, 100);
    },

    // 🆕 Get animation styles for a specific announcement
    getAnimationStyles(announcementId) {
      const state = this.animationStates.get(announcementId);
      if (!state) {
        return {
          opacity: 1,
          transform: "translateX(0) scale(1)",
          transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
        };
      }

      return {
        opacity: state.opacity,
        transform: state.transform,
        transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
        boxShadow:
          state.boxShadow ||
          (this.isMobile
            ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
            : "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"),
      };
    },

    // 🆕 Handle button hover effects inline
    handleButtonHover(event, isHovering) {
      const baseColor = this.bubbleColor || "#1a1a1a";
      if (isHovering) {
        const darkerColor = this.darkenColor(baseColor, 10);
        event.target.style.backgroundColor = darkerColor;
        event.target.style.opacity = "0.9";
      } else {
        event.target.style.backgroundColor = baseColor;
        event.target.style.opacity = "1";
      }
    },

    // 🆕 Handle box hover effects inline
    handleBoxHover(event, isHovering) {
      if (isHovering) {
        event.currentTarget.style.backgroundColor = "#f9fafb"; // gray-50
      } else {
        event.currentTarget.style.backgroundColor = "transparent";
      }
    },

    // 🆕 Handle close button hover effects inline
    handleCloseHover(event, isHovering) {
      if (isHovering) {
        event.target.style.color = "#4b5563"; // gray-600
        event.target.style.backgroundColor = "#f3f4f6"; // gray-100
      } else {
        event.target.style.color = "#9ca3af"; // gray-400
        event.target.style.backgroundColor = "transparent";
      }
    },

    // 🆕 Animate announcement in
    animateIn(announcementId) {
      // Start with hidden state
      this.animationStates.set(announcementId, {
        opacity: 0,
        transform: "translateX(100%) scale(0.9)",
        isEntering: true,
        isLeaving: false,
      });

      // Force update to apply initial state
      this.$forceUpdate();

      // Animate to visible state after a small delay
      setTimeout(() => {
        const state = this.animationStates.get(announcementId);
        if (state && state.isEntering) {
          this.animationStates.set(announcementId, {
            opacity: 1,
            transform: "translateX(0) scale(1)",
            isEntering: false,
            isLeaving: false,
          });
          this.$forceUpdate();
        }
      }, 50);
    },

    // 🆕 Animate announcement out
    animateOut(announcementId, callback) {
      const state = this.animationStates.get(announcementId);
      if (state) {
        this.animationStates.set(announcementId, {
          opacity: 0,
          transform: "translateX(100%) scale(0.9)",
          isEntering: false,
          isLeaving: true,
        });
        this.$forceUpdate();

        // Remove from DOM after animation
        setTimeout(() => {
          this.animationStates.delete(announcementId);
          if (callback) callback();
        }, 400);
      } else {
        if (callback) callback();
      }
    },

    // 🆕 Start subtle shimmer animation for cards
    startShimmerAnimation() {
      if (this.animationInterval) return;

      let shimmerStep = 0;
      this.animationInterval = setInterval(() => {
        // Create subtle shimmer effect on announcement cards
        this.visibleAnnouncements.forEach((announcement, index) => {
          const delay = index * 200; // Stagger the shimmer
          if ((shimmerStep + delay) % 2000 < 100) {
            // Add subtle glow effect
            const state = this.animationStates.get(announcement.id);
            if (state && !state.isEntering && !state.isLeaving) {
              state.boxShadow = "0 4px 20px rgba(59, 130, 246, 0.15)";
              this.$forceUpdate();
            }
          } else {
            // Remove glow effect
            const state = this.animationStates.get(announcement.id);
            if (state && !state.isEntering && !state.isLeaving) {
              state.boxShadow = undefined;
              this.$forceUpdate();
            }
          }
        });

        shimmerStep += 100;
      }, 100);
    },

    // 🆕 Stop shimmer animation
    stopShimmerAnimation() {
      if (this.animationInterval) {
        clearInterval(this.animationInterval);
        this.animationInterval = null;
      }
    },

    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },

    debugTimingInfo() {
      const now = Date.now();
      const closedTime = this.widgetClosedTime || now;
    },

    startAnnouncementTimers() {
      this.clearAllTimers();
      this.debugTimingInfo();

      this.announcements.forEach((announcement) => {
        const delaySeconds =
          typeof announcement.delay === "string"
            ? parseInt(announcement.delay, 10)
            : announcement.delay || 0;

        if (delaySeconds > 0) {
          const timer = setTimeout(() => {
            this.forceUpdate();
          }, delaySeconds * 1000);

          this.timers.push(timer);
        }
      });

      const generalTimer = setInterval(() => {
        if (this.isWidgetOpen) {
          this.clearAllTimers();
          return;
        }

        this.forceUpdate();
      }, 1000);

      this.timers.push(generalTimer);

      setTimeout(() => {
        this.clearAllTimers();
      }, 300000);
    },

    forceUpdate() {
      this.forceUpdateTrigger++;
      this.$forceUpdate();
    },

    clearAllTimers() {
      this.timers.forEach((timer) => {
        clearTimeout(timer);
        clearInterval(timer);
      });
      this.timers = [];
    },

    dismissAnnouncement(announcementId) {
      // 🆕 Animate out before dismissing
      this.animateOut(announcementId, () => {
        this.dismissedAnnouncements.push(announcementId);

        try {
          localStorage.setItem(
            "chatly_dismissed_announcements",
            JSON.stringify(this.dismissedAnnouncements)
          );
        } catch (error) {
          console.warn("Could not save dismissed announcements:", error);
        }

        this.forceUpdate();
      });
    },

    openLink(url) {
      if (url) {
        window.open(url, "_blank");
      }
    },

    handleFaqClick(announcement) {
      // 🆕 ENHANCED: Create proper FAQ data structure for direct navigation
      const faqData = {
        $id: announcement.faqId,
        question: announcement.faqTitle,
        // We don't have the full answer content in announcements, so we'll fetch it
        answer: "Loading...",
        category: announcement.category || "General",
      };

      // 🎯 Emit with proper structure for direct FAQ navigation
      this.$emit("navigate-to-faq", {
        faqData: faqData,
        category: announcement.category,
        action: "navigate-to-faq",
        directToFaq: true, // This tells FAQScreen to go directly to detail view
        faqId: announcement.faqId,
        faqTitle: announcement.faqTitle,
      });
    },

    loadDismissedAnnouncements() {
      try {
        const dismissed = localStorage.getItem(
          "chatly_dismissed_announcements"
        );
        if (dismissed) {
          this.dismissedAnnouncements = JSON.parse(dismissed);
        }
      } catch (error) {
        console.warn("Could not load dismissed announcements:", error);
      }
    },

    // 🆕 NEW: Helper function to darken color for hover effect
    darkenColor(color, percent) {
      // Handle hex colors
      if (color.startsWith("#")) {
        const num = parseInt(color.slice(1), 16);
        const amt = Math.round(2.55 * percent);
        const R = (num >> 16) - amt;
        const G = ((num >> 8) & 0x00ff) - amt;
        const B = (num & 0x0000ff) - amt;
        return `#${(
          0x1000000 +
          (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
          (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
          (B < 255 ? (B < 1 ? 0 : B) : 255)
        )
          .toString(16)
          .slice(1)}`;
      }

      // Handle rgb/rgba colors (basic support)
      if (color.startsWith("rgb")) {
        return color; // Return as-is for now
      }

      // Fallback for other color formats
      return color;
    },
  },
  created() {},
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
